import RMTable from 'components/customComponents/Controls/RMTable';
import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import TableSortInputFilter from 'components/customComponents/Controls/tableSortInputFilter';
import useIngredientsList from './hooks/useIngredientsList';
import { setIngredientSelectedRows } from 'redux/reducers/ingredientTableReducer';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import 'components/ingredients/IngredientsTable.css';
import { IngredientTableRow } from 'business/models/Models';

const IngredientsTable = (props) => {
  const { setShowLoadingModal, setFilteredIngredientRowsIndex } = props;
  const dispatch = useRMDispatch();
  const tableData: IngredientTableRow[] = useIngredientsList();
  const [selectedRows, setSelectedRows] = useState([]);
  const _selectedRows = useRMSelector((state) => state.ingredientsTable.selectedRows);
  const handleTableFilter = (filterable: boolean, filterTitle: string) => (event: any) =>
    (
      <TableSortInputFilter
        event={event}
        title={filterTitle}
        showSearchInput={filterable}
      />
    );

  useEffect(() => {
    setShowLoadingModal(false);
  }, [tableData]);

  useEffect(() => {
    if (!_selectedRows || _selectedRows.length < 1) {
      setSelectedRows([]);
    }
  }, [_selectedRows]);

  const columns: {
    accessor: string;
    Header: string;
    Filter?: (event: any) => JSX.Element;
    className: string;
  }[] = useMemo(
    () => [
      {
        accessor: 'id',
        Header: 'ID',
        Filter: handleTableFilter(true, 'ID'),
        className: 'idColumnWidth columnPaddingRight',
      },
      {
        accessor: 'type',
        Header: 'Type',
        Filter: handleTableFilter(true, 'Type'),
        className: 'typeColumnWidth columnPaddingRight',
      },
      {
        accessor: 'name',
        Header: 'Name',
        Filter: handleTableFilter(true, 'Name'),
        className: 'nameColumnWidth columnPaddingRight',
      },
      {
        accessor: 'shortName',
        Header: 'Short Name',
        Filter: handleTableFilter(true, 'Short Name'),
        className: 'columnWidth columnPaddingRight',
      },
      {
        accessor: 'defaultStockNumber',
        Header: 'Default Stock Number',
        Filter: handleTableFilter(true, 'Default Stock Number'),
        className: 'defaultStockColumnWidth columnPaddingRight',
      },
      {
        accessor: 'concUnit',
        Header: 'Conc. Unit',
        Filter: handleTableFilter(true, 'Conc. Unit'),
        className: 'columnWidth columnPaddingRight',
      },
      {
        accessor: 'stockConc',
        Header: 'Stock Conc.',
        Filter: handleTableFilter(true, 'Stock Conc.'),
        className: 'columnWidth columnPaddingRight',
      },
      {
        accessor: 'lowConc',
        Header: 'Low Conc.',
        Filter: handleTableFilter(true, 'Low Conc.'),
        className: 'columnWidth columnPaddingRight',
      },
      {
        accessor: 'highConc',
        Header: 'High Conc.',
        Filter: handleTableFilter(true, 'High Conc.'),
        className: 'columnWidth columnPaddingRight',
      },
      {
        accessor: 'liquidClassName',
        Header: 'Liquid Class',
        Filter: handleTableFilter(true, 'Liquid Class'),
        className: 'columnWidth columnPaddingRight',
      },
      {
        accessor: 'stockMinPh',
        Header: 'Stock Min. pH',
        Filter: handleTableFilter(true, 'Stock Min. pH'),
        className: 'stockMinMaxColumnWidth columnPaddingRight',
      },
      {
        accessor: 'stockMaxPh',
        Header: 'Stock Max. pH',
        Filter: handleTableFilter(true, 'Stock Max. pH'),
        className: 'stockMinMaxColumnWidth columnPaddingRight',
      },
    ],
    [selectedRows],
  );

  const handleSelectionChange = useCallback(
    (rowIndices) => {
      setSelectedRows([...rowIndices]);
      const selectedTableRows: IngredientTableRow[] = [];
      rowIndices.forEach((rowInd) => {
        selectedTableRows.push(tableData[rowInd]);
      });
      dispatch(setIngredientSelectedRows(selectedTableRows));
    },
    [tableData],
  );

  return (
    <div id="ingredientstable-wrapper" style={{ height: '89%', overflow: 'auto' }}>
      <RMTable
        columns={columns}
        data={tableData}
        selectedIds={selectedRows}
        onCheckboxSelectionChange={handleSelectionChange}
        onRowClicked={(row) => {
          handleSelectionChange([row.index]);
        }}
        enableMultiSelection
        setFilteredRowsIndex={setFilteredIngredientRowsIndex}
      />
    </div>
  );
};

export default memo(IngredientsTable);
